// The redstone engine (RED-1/2/4, M15.1): owns the dirty set + every delayed
// component's cross-tick state (repeater/comparator flip timers, torch burnout,
// button auto-release), floods each dirty network, and drives the combinational
// PowerField over it once per redstone tick. PURE (ENG-1) — the host adapts the
// world. Contract: docs/redstone-semantics.md.
import { RED } from '../../config';
import { PowerField, comparatorOutput } from './power';
import { cellKey, type Dir, type Facing, type RedstoneWorld } from './types';

type Cell = readonly [number, number, number];

interface Flip { want: boolean; timer: number } // repeater
interface CFlip { want: number; timer: number } // comparator
interface TorchSt { want: boolean; timer: number; history: number[]; cooldown: number }

export class RedstoneEngine {
  private readonly dirty = new Set<string>();
  private readonly repeater = new Map<string, Flip>();
  private readonly comparator = new Map<string, CFlip>();
  private readonly comparatorOut = new Map<string, number>();
  private readonly torch = new Map<string, TorchSt>();
  private readonly button = new Map<string, number>(); // rt until auto-release
  private rt = 0;

  constructor(private readonly world: RedstoneWorld) {}

  /** Mark a cell (and its 6 neighbours) for re-solve — call on any block edit. */
  markDirty(x: number, y: number, z: number): void {
    this.dirty.add(cellKey(x, y, z));
    this.dirty.add(cellKey(x + 1, y, z)); this.dirty.add(cellKey(x - 1, y, z));
    this.dirty.add(cellKey(x, y + 1, z)); this.dirty.add(cellKey(x, y - 1, z));
    this.dirty.add(cellKey(x, y, z + 1)); this.dirty.add(cellKey(x, y, z - 1));
  }

  /** Press a stone button: powers it now, auto-releases after RED.BUTTON_TICKS. */
  pressButton(x: number, y: number, z: number): void {
    this.world.setPowered(x, y, z, true);
    this.button.set(cellKey(x, y, z), RED.BUTTON_TICKS);
    this.markDirty(x, y, z);
  }

  /** Nothing scheduled and nothing dirty → idle (host can skip). */
  get idle(): boolean {
    return this.dirty.size === 0 && this.repeater.size === 0 && this.comparator.size === 0
      && this.torch.size === 0 && this.button.size === 0;
  }

  /** The redstone power (0..15) currently reaching the block at (x,y,z). Used by
   *  the host to lock hoppers and edge-trigger droppers/dispensers (RED-2). Builds
   *  a bounded local field from the cell + its neighbours; mutates no engine state. */
  powerLevelAt(x: number, y: number, z: number): number {
    const seeds: Cell[] = [
      [x, y, z], [x + 1, y, z], [x - 1, y, z],
      [x, y + 1, z], [x, y - 1, z], [x, y, z + 1], [x, y, z - 1],
    ];
    const cells = this.flood(seeds);
    const field = new PowerField(this.world, cells, (cx, cy, cz) => this.comparatorOut.get(cellKey(cx, cy, cz)) ?? 0);
    return field.blockPower(x, y, z);
  }

  /** Advance one redstone tick: solve combinational, step delayed components. */
  tick(): void {
    if (this.idle) return;
    this.rt++;
    this.tickButtons();
    // Keep delayed components under evaluation until they settle.
    for (const k of this.repeater.keys()) this.dirty.add(k);
    for (const k of this.comparator.keys()) this.dirty.add(k);
    for (const k of this.torch.keys()) this.dirty.add(k);
    if (this.dirty.size === 0) return;

    const seeds = [...this.dirty].map(parse);
    this.dirty.clear();
    const cells = this.flood(seeds);
    const field = new PowerField(this.world, cells, (x, y, z) => this.comparatorOut.get(cellKey(x, y, z)) ?? 0);

    // Combinational layer: wire power + lamps settle this tick.
    for (const [x, y, z] of cells) {
      const r = this.world.roleAt(x, y, z);
      if (r.kind === 'wire') {
        const p = field.wirePower(x, y, z);
        if (p !== r.power) { this.world.setWire(x, y, z, p); this.markDirty(x, y, z); }
      } else if (r.kind === 'lamp') {
        const lit = field.blockPower(x, y, z) > 0;
        if (lit !== r.lit) { this.world.setLit(x, y, z, lit); }
      }
    }
    // Delayed layer: repeaters, comparators, torches.
    for (const [x, y, z] of cells) {
      const r = this.world.roleAt(x, y, z);
      if (r.kind === 'repeater') this.stepRepeater(field, x, y, z, r.facing, r.delay, r.powered);
      else if (r.kind === 'comparator') this.stepComparator(field, x, y, z, r.facing, r.mode, r.powered);
      else if (r.kind === 'torch') this.stepTorch(field, x, y, z, r.attach, r.lit);
    }
  }

  private tickButtons(): void {
    for (const [k, t] of this.button) {
      if (t <= 1) {
        const [x, y, z] = parse(k);
        this.world.setPowered(x, y, z, false);
        this.button.delete(k);
        this.markDirty(x, y, z);
      } else {
        this.button.set(k, t - 1);
      }
    }
  }

  private stepRepeater(
    field: PowerField, x: number, y: number, z: number,
    facing: Facing, delay: number, powered: boolean,
  ): void {
    const k = cellKey(x, y, z);
    const locked = field.repeaterLocked(x, y, z, facing);
    const desired = locked ? powered : field.repeaterInput(x, y, z, facing) > 0;
    if (desired === powered) { this.repeater.delete(k); return; }
    const st = this.repeater.get(k);
    if (!st || st.want !== desired) { this.repeater.set(k, { want: desired, timer: Math.max(1, delay) }); return; }
    if (--st.timer <= 0) {
      this.world.setPowered(x, y, z, desired);
      this.repeater.delete(k);
      this.markDirty(x, y, z);
    }
  }

  private stepComparator(
    field: PowerField, x: number, y: number, z: number,
    facing: Facing, mode: 'compare' | 'subtract', powered: boolean,
  ): void {
    const k = cellKey(x, y, z);
    const out = comparatorOutput(field.comparatorRear(x, y, z, facing), field.comparatorSide(x, y, z, facing), mode);
    const cur = this.comparatorOut.get(k) ?? 0;
    if (out === cur) {
      this.comparator.delete(k);
      if (powered !== out > 0) this.world.setPowered(x, y, z, out > 0); // keep flag consistent
      return;
    }
    const st = this.comparator.get(k);
    if (!st || st.want !== out) { this.comparator.set(k, { want: out, timer: RED.COMPARATOR_DELAY }); return; }
    if (--st.timer <= 0) {
      this.comparatorOut.set(k, out);
      this.comparator.delete(k);
      if (powered !== out > 0) this.world.setPowered(x, y, z, out > 0);
      this.markDirty(x, y, z);
    }
  }

  private stepTorch(
    field: PowerField, x: number, y: number, z: number,
    attach: Dir, lit: boolean,
  ): void {
    const k = cellKey(x, y, z);
    const st = this.torch.get(k);
    // Burned out: held off until the cooldown expires, ignoring its input.
    if (st && st.cooldown > 0) {
      st.cooldown--;
      if (lit) { this.world.setLit(x, y, z, false); this.markDirty(x, y, z); }
      return;
    }
    const desired = !field.torchAttachPowered(x, y, z, attach);
    if (desired === lit) { if (st) this.torch.delete(k); return; } // settled → drop history
    if (!st) { this.torch.set(k, { want: desired, timer: RED.TORCH_DELAY, history: [], cooldown: 0 }); return; }
    st.history = st.history.filter((t) => this.rt - t < RED.BURNOUT_WINDOW);
    if (st.want !== desired) { st.want = desired; st.timer = RED.TORCH_DELAY; return; }
    if (--st.timer > 0) return;
    // Fire the flip; record it for burnout accounting (anti-clock, RED-2).
    st.history.push(this.rt);
    if (st.history.length >= RED.BURNOUT_CHANGES) {
      st.cooldown = RED.BURNOUT_OFF;
      st.history = [];
      this.world.setLit(x, y, z, false);
      this.markDirty(x, y, z);
      return;
    }
    this.world.setLit(x, y, z, desired);
    this.markDirty(x, y, z);
    st.timer = RED.TORCH_DELAY;
  }

  /** Collect the redstone network reachable from the dirty seeds (bounded). Dust
   *  and components expand freely; a conductor is a one-hop bridge (dust ⇄ block
   *  ⇄ dust) so the flood can't run away through solid terrain. */
  private flood(seeds: readonly Cell[]): Cell[] {
    const visited = new Set<string>();
    const result: Cell[] = [];
    const queue: Cell[] = [...seeds];
    while (queue.length && result.length < RED.MAX_NETWORK) {
      const c = queue.shift()!;
      const k = cellKey(c[0], c[1], c[2]);
      if (visited.has(k)) continue;
      visited.add(k);
      result.push(c);
      const role = this.world.roleAt(c[0], c[1], c[2]);
      const relevant = role.kind !== 'air' && role.kind !== 'block';
      const conductor = this.world.isConductor(c[0], c[1], c[2]);
      if (!relevant && !conductor) continue;
      for (const [dx, dy, dz] of NEIGH) {
        const n: Cell = [c[0] + dx, c[1] + dy, c[2] + dz];
        if (visited.has(cellKey(n[0], n[1], n[2]))) continue;
        const nr = this.world.roleAt(n[0], n[1], n[2]);
        const nRelevant = nr.kind !== 'air' && nr.kind !== 'block';
        const nConductor = this.world.isConductor(n[0], n[1], n[2]);
        if (relevant ? (nRelevant || nConductor) : nRelevant) queue.push(n);
      }
    }
    return result;
  }
}

const NEIGH: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
];
const parse = (k: string): Cell => { const [x, y, z] = k.split(',').map(Number); return [x!, y!, z!]; };
